import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon, Globe, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';

const Navbar = ({ lang, setLang, isDark, setIsDark, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Bloqueo de scroll cuando el menú móvil está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const projectsList = [
    { name: "Kanan", tag: lang === 'es' ? "Seguridad & Red Cívica" : "Civic Safety & Network", path: "/proyectos/kanan" },
    { name: "Blebe", tag: lang === 'es' ? "Apoyo en Selección" : "Hiring Vetting", path: "/proyectos/Blebe" },
    { name: "Marchantite", tag: lang === 'es' ? "Mercados Locales" : "Local Markets", path: "/proyectos/Marchantite" },
    { name: "Niya", tag: lang === 'es' ? "Salud Financiera" : "Financial Clarity", path: "/proyectos/Niya" }
  ];

  return (
    <nav className={`nav-container ${scrolled ? 'nav-scrolled' : ''}`}>
      <div className="nav-wrapper">
        <div className={`nav-glass-card ${isDark ? 'glass-dark' : 'glass-light'}`}>

          {/* LOGO */}
          <div className="nav-logo">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/logo192.png"
                alt="TDSMX Logo"
                className="logo-img w-8 h-8 object-contain"
              />
              <span className={`font-black text-lg tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                TDSMX
              </span>
            </Link>
          </div>

          {/* NAVEGACIÓN DESKTOP */}
          <div className="nav-desktop">
            <Link
              to="/"
              className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
            >
              <span className="nav-link-text">{t?.nav?.home || (lang === 'es' ? 'Inicio' : 'Home')}</span>
              <span className="nav-indicator" />
            </Link>

            {/* INICIATIVAS (Dropdown interactivo) */}
            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button
                className={`nav-link flex items-center gap-1 cursor-pointer ${isDropdownOpen || location.pathname.startsWith('/proyectos') ? 'active' : ''}`}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="nav-link-text">{lang === 'es' ? 'Iniciativas' : 'Platforms'}</span>
                <ChevronDown size={14} className={`transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                <span className="nav-indicator" />
              </button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.96 }}
                    transition={{ duration: 0.18 }}
                    className={`absolute left-0 mt-2 w-56 rounded-2xl p-2 border backdrop-blur-3xl shadow-2xl z-50 ${
                      isDark ? 'bg-slate-900/95 border-white/10 text-white' : 'bg-white/95 border-slate-200 text-slate-900'
                    }`}
                  >
                    {projectsList.map((project) => (
                      <Link
                        key={project.path}
                        to={project.path}
                        className={`block px-4 py-2.5 rounded-xl transition-all ${
                          isDark ? 'hover:bg-white/5 hover:text-blue-400' : 'hover:bg-blue-50/70 hover:text-blue-600'
                        }`}
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <span className="text-xs font-black uppercase tracking-wider block">{project.name}</span>
                        <span className="text-[9px] font-bold opacity-50 uppercase tracking-widest">{project.tag}</span>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/nosotros"
              className={`nav-link ${location.pathname === '/nosotros' ? 'active' : ''}`}
            >
              <span className="nav-link-text">{t?.nav?.about || (lang === 'es' ? 'Nosotros' : 'About')}</span>
              <span className="nav-indicator" />
            </Link>
          </div>

          {/* ACCIONES DE IDIOMA Y TEMA */}
          <div className="nav-actions flex items-center gap-2">
            <div className={`hidden md:flex items-center p-0.5 rounded-xl border ${
              isDark ? 'bg-slate-800/60 border-white/5' : 'bg-slate-200/60 border-slate-300'
            }`}>
              <button
                onClick={() => setLang('es')}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                  lang === 'es'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                }`}
              >
                ES
              </button>
              <button
                onClick={() => setLang('en')}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                  lang === 'en'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                }`}
              >
                EN
              </button>
            </div>

            <button onClick={() => setIsDark(!isDark)} className="icon-btn p-2 rounded-xl border border-current/10">
              {isDark ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} />}
            </button>

            <button className="mobile-toggle md:hidden p-2 rounded-xl" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* MENÚ MÓVIL EN PANTALLA COMPLETA */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className={`fixed inset-0 z-[99999] md:hidden flex flex-col justify-between p-6 backdrop-blur-3xl overflow-y-auto ${
              isDark ? 'bg-[#0A0F1A]/95 text-white' : 'bg-[#F8FAFC]/95 text-slate-900'
            }`}
          >
            {/* Header: Marca + Selector de tema + Botón Cerrar */}
            <div className="flex items-center justify-between pb-6 border-b border-current/10 shrink-0">
              <div className="flex items-center gap-2.5">
                <img
                  src="/logo192.png"
                  alt="TDSMX Logo"
                  className="w-8 h-8 object-contain"
                />
                <span className="font-black text-xl tracking-tight">TDSMX</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsDark(!isDark)}
                  className={`p-2.5 rounded-xl border ${
                    isDark ? 'bg-slate-800/60 border-white/10' : 'bg-slate-100 border-slate-200'
                  }`}
                >
                  {isDark ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className={`p-2.5 rounded-xl border ${
                    isDark ? 'bg-slate-800/60 border-white/10' : 'bg-slate-100 border-slate-200'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Enlaces Principales & Lista de Iniciativas */}
            <div className="flex flex-col gap-6 py-6 text-left">
              <div className="flex flex-col gap-4">
                <Link
                  to="/"
                  className="text-2xl font-black tracking-tight hover:text-blue-500 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {t?.nav?.home || (lang === 'es' ? 'Inicio' : 'Home')}
                </Link>
                <Link
                  to="/nosotros"
                  className="text-2xl font-black tracking-tight hover:text-blue-500 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {t?.nav?.about || (lang === 'es' ? 'Nosotros' : 'About')}
                </Link>
              </div>

              <div className="pt-6 border-t border-current/10 flex flex-col gap-3">
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-500">
                  {lang === 'es' ? 'Iniciativas' : 'Platforms'}
                </span>

                <div className="flex flex-col gap-2.5">
                  {projectsList.map((project) => (
                    <Link
                      key={project.path}
                      to={project.path}
                      className={`p-4 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                        isDark
                          ? 'bg-slate-900/50 border-white/5 active:bg-slate-800/70'
                          : 'bg-white border-slate-200 active:bg-slate-100 shadow-sm'
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      <span className="text-sm font-black tracking-tight">{project.name}</span>
                      <span className="text-[9px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 shrink-0">
                        {project.tag}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Selector de idioma inferior segmentado */}
            <div className="pt-6 border-t border-current/10 shrink-0 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-blue-500" />
                <span className="text-xs font-bold opacity-75">
                  {lang === 'es' ? 'Idioma' : 'Language'}
                </span>
              </div>

              <div className={`p-1 rounded-xl border flex items-center gap-1 ${
                isDark ? 'bg-slate-800/80 border-white/5' : 'bg-slate-200/70 border-slate-300'
              }`}>
                <button
                  onClick={() => {
                    setLang('es');
                    setIsOpen(false);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                    lang === 'es'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                  }`}
                >
                  ES
                </button>
                <button
                  onClick={() => {
                    setLang('en');
                    setIsOpen(false);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                    lang === 'en'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                  }`}
                >
                  EN
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;